import type { TagJSONObject } from '@ikegps/core-api';
import { IkeToken } from '@ikegps/core-api';
import { AutoCompleteMenu, type SelectAutoCompleteOption } from '../../components/autocomplete.menu/autocomplete.menu';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { DRAGON_ID, DRAGON_TYPE, DragonDomTypes } from '../../dom';
import { T } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { AppStore } from '../../store';
import { PhotomeasureButton } from '../photomeasure.button/photomeasure.button';
import { TagCardCheckbox } from '../tag.card.checkbox/tag.card.checkbox';
import { BoundingString } from '../tag.map/bounding.string';
import { TagNameInput, TagNameReadonlyInput } from '../tag.name.input/tag.name.input';
import { TagNameInputController } from '../tag.name.input/tag.name.input.ctrl';
import { TopologicalEditorButton } from '../topological.editor.button/topological.editor.button';
import { CSS, Style } from './tag.card.style';

addStyles(Style);

export class TagCard {
	state: TagState;
	tag: TagJSONObject;
	ctrl: TagNameInputController;
	autoCompleteMenu: AutoCompleteMenu = null;

	constructor(state: TagState, tag: TagJSONObject) {
		this.state = state;
		this.tag = tag;
		this.ctrl = new TagNameInputController(this.state, tag);
		this.autoCompleteMenu = new AutoCompleteMenu({
			inputState: this.ctrl.getInputState(),
			options: () => this.ctrl.autoCompleteOptions,
			onselect: (option: SelectAutoCompleteOption) => this.ctrl.updateEdit(option.value),
		});
	}

	view(): MithrilVNode {
		const isHovered = CurrentState.hoverId === this.tag.objectId;

		const className = `${CSS.container} ${isHovered ? CSS.isHovered : ''}`;

		const summary = AppStore.TagSummary.get(this.tag.objectId);
		const hasSummary = summary != null;

		const editing = this.state.edit.isEditingTag(this.tag.objectId);
		const renaming = this.state.edit.isRenamingTag(this.tag.objectId);

		const showEditButtons =
			!this.state.edit.isEditingTag() && !this.state.edit.isRenamingTag() && !CurrentState.CollectionEdit.isDirty;

		const toolbar = this.makeToolbar();

		const collectionsSelected = CurrentState.Tag.collectionsSelectedSize(this.tag.objectId);

		const selectedState = {
			isSelected: () => collectionsSelected === summary.revisions.length,
			setSelected: (selected) => CurrentState.Tag.selectTag(this.tag.objectId, selected),
			isIndeterminate: () => collectionsSelected > 0 && collectionsSelected < summary.revisions.length,
		};

		const children = [
			hasSummary ? TagCardCheckbox.view(this.state, selectedState, this.tag, summary) : null,
			editing ? TagNameInput.view(this.ctrl, this.autoCompleteMenu) : TagNameReadonlyInput.view(this.ctrl),
			renaming ? Spinner.view() : null,
			showEditButtons ? toolbar : null,
		];

		return m(
			'div',
			{
				className,
				key: this.tag.objectId,
				[DRAGON_ID]: this.tag.objectId,
				[DRAGON_TYPE]: DragonDomTypes.TAG,
			},
			children
		);
	}

	private makeToolbar(): MithrilVNode {
		const canEdit = Services.Token.has(IkeToken.TAG_JOB_UPDATE);

		const tagCenterObj = AppStore.TagSummary.get(this.tag.objectId)?.center;
		if (!tagCenterObj) {
			return null;
		}

		const tagCenterString = BoundingString.toString(tagCenterObj, 15);

		var children = [
			Services.Token.has(IkeToken.FEATURE_MAP_MODE) &&
				!Services.UserProfile.isReadOnly(Services.UserProfile.get(Services.User.current.objectId)) &&
				TopologicalEditorButton.view(this.tag.objectId, tagCenterString),
			PhotomeasureButton.view(this.tag.objectId),
			canEdit &&
				ImageButton.view(MaterialIcon.EDIT, {
					title: T.LINK_EDIT_TAG,
					className: CSS.renamejob,
					onclick: () => this.state.edit.editTag(this.tag.objectId, this.tag.name),
				}),
		];

		return m('div', { className: CSS.toolbar }, children);
	}
}
